import * as React from 'react';
import { view, prop } from '@vscode/observables-react';
import type { ReportFixtureEntry } from '../report/ScreenshotReport.js';
import type { ReportModel } from '../report/ReportModel.js';

export interface ReportFixtureItem {
  readonly fixtureId: string;
  readonly name: string;
  readonly path: string;
  readonly entry: ReportFixtureEntry;
}

// ---------------------------------------------------------------------------
// Checkered canvas (same as FixturePreviewItem)
// ---------------------------------------------------------------------------

const CHECKER_SIZE = 8;

function createCheckerPattern(baseColor: string, checkerColor: string): string {
  return `
    linear-gradient(45deg, ${checkerColor} 25%, transparent 25%),
    linear-gradient(-45deg, ${checkerColor} 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, ${checkerColor} 75%),
    linear-gradient(-45deg, transparent 75%, ${checkerColor} 75%)
  `;
}

const lightPattern = {
  backgroundColor: '#ffffff',
  backgroundImage: createCheckerPattern('#ffffff', '#e5e5e5'),
};

const darkPattern = {
  backgroundColor: '#1e1e1e',
  backgroundImage: createCheckerPattern('#1e1e1e', '#2d2d2d'),
};

const canvasBaseStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '12px',
  borderRadius: '4px',
  backgroundSize: `${CHECKER_SIZE * 2}px ${CHECKER_SIZE * 2}px`,
  backgroundPosition: `0 0, 0 ${CHECKER_SIZE}px, ${CHECKER_SIZE}px -${CHECKER_SIZE}px, -${CHECKER_SIZE}px 0`,
  boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
};

function getCanvasStyle(background: 'light' | 'dark'): React.CSSProperties {
  const pattern = background === 'dark' ? darkPattern : lightPattern;
  return {
    ...canvasBaseStyle,
    backgroundColor: pattern.backgroundColor,
    backgroundImage: pattern.backgroundImage,
  };
}

// ---------------------------------------------------------------------------
// Screenshot image (natural size, no scaling)
// ---------------------------------------------------------------------------

function ScreenshotImage({ src, alt, background }: {
  src: string;
  alt: string;
  background: 'light' | 'dark';
}) {
  return (
    <div style={getCanvasStyle(background)}>
      <img src={src} alt={alt} style={styles.image} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single fixture comparison
// ---------------------------------------------------------------------------

const ReportFixtureView = view(
  {
    item: prop.const<ReportFixtureItem>(),
    reportModel: prop.const<ReportModel>(),
  },
  (_reader, props) => {
    const { item, reportModel } = props;
    const { entry } = item;
    const background = entry.background ?? 'light';

    const statusBadge = entry.status === 'unchanged'
      ? <span style={styles.badgeMatch}>✓ match</span>
      : entry.status === 'changed'
        ? <span style={styles.badgeChanged}>● changed</span>
        : <span style={styles.badgeAdded}>+ added</span>;

    const hasScreenshots = entry.baselineScreenshot || entry.currentScreenshot;

    return (
      <div style={styles.fixtureRow}>
        <div style={styles.fixtureLabel}>
          <span style={styles.fixtureName}>{item.fixtureId}</span>
          {statusBadge}
        </div>
        {hasScreenshots && (
          <div style={styles.sideBySide}>
            {entry.baselineScreenshot && (
              <div style={styles.screenshotColumn}>
                <span style={styles.columnLabel}>Baseline</span>
                <ScreenshotImage
                  src={reportModel.resolveScreenshotUrl(entry.baselineScreenshot)}
                  alt={`Baseline screenshot of ${item.fixtureId}`}
                  background={background}
                />
              </div>
            )}
            {entry.currentScreenshot && (
              <div style={styles.screenshotColumn}>
                <span style={styles.columnLabel}>Current</span>
                <ScreenshotImage
                  src={reportModel.resolveScreenshotUrl(entry.currentScreenshot)}
                  alt={`Current screenshot of ${item.fixtureId}`}
                  background={background}
                />
              </div>
            )}
          </div>
        )}
        {!hasScreenshots && entry.status === 'unchanged' && (
          <div style={styles.hashRow}>
            <span style={styles.hashText}>hash: {entry.targetHash}</span>
          </div>
        )}
      </div>
    );
  }
);

export const ReportComparisonRow = view(
  {
    name: prop.const<string>(),
    path: prop.const<string>(),
    items: prop.const<readonly ReportFixtureItem[]>(),
    reportModel: prop.const<ReportModel>(),
  },
  (_reader, props) => {
    return (
      <div style={styles.groupContent}>
        {props.items.map((item) => (
          <ReportFixtureView
            key={item.fixtureId}
            item={item}
            reportModel={props.reportModel}
          />
        ))}
      </div>
    );
  }
);

const styles = {
  groupHeader: {
    marginBottom: 12,
  } as React.CSSProperties,
  groupTitle: {
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--component-explorer-foreground)',
  } as React.CSSProperties,
  groupContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  } as React.CSSProperties,
  fixtureRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  } as React.CSSProperties,
  fixtureLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  } as React.CSSProperties,
  fixtureName: {
    fontSize: 12,
    fontWeight: 500,
    color: 'var(--component-explorer-foreground)',
  } as React.CSSProperties,
  sideBySide: {
    display: 'flex',
    flexDirection: 'row',
    gap: 16,
  } as React.CSSProperties,
  screenshotColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  } as React.CSSProperties,
  columnLabel: {
    fontSize: 11,
    fontWeight: 500,
    color: 'var(--component-explorer-descriptionForeground)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  } as React.CSSProperties,
  image: {
    display: 'block',
  } as React.CSSProperties,
  hashRow: {
    padding: '8px 0',
  } as React.CSSProperties,
  hashText: {
    fontSize: 11,
    color: 'var(--component-explorer-descriptionForeground)',
    fontFamily: 'monospace',
  } as React.CSSProperties,
  badgeMatch: {
    fontSize: 11,
    color: 'var(--component-explorer-testing-iconPassed)',
    fontWeight: 500,
  } as React.CSSProperties,
  badgeChanged: {
    fontSize: 11,
    color: 'var(--component-explorer-testing-iconFailed)',
    fontWeight: 500,
  } as React.CSSProperties,
  badgeAdded: {
    fontSize: 11,
    color: 'var(--component-explorer-testing-iconQueued)',
    fontWeight: 500,
  } as React.CSSProperties,
};
